<template>
    <div class="block-input illustration">
        <div class="canvas">
            <!-- White gap -->
            <div class="base-gap" :style="{'height': `${getBaseGapHeight()}px`}"></div>
            <!--Block names-->
            <div class="layer block-names">
                <div class="top-name">
                    Part
                </div>
                <div class="bottom-name" v-if="buildPlate">
                    Build plate
                </div>
                <div class="bottom-name" v-else>
                    Part
                </div>
            </div>
            <!-- Legends -->
            <div class="layer legends">
                <div class="fill top">
                    <div class="legend depth" :style="{'height': `${getLegendHeight('top-depth')}px`, 'display': isDepthVisible('top')}">
                        <div class="legend-title">P<sub>t</sub></div>
                        <div class="legend-line"></div>
                    </div>
                </div>
                <div class="legend ot" :style="{'height': `${getLegendHeight('top-rect')}px`}">
                    <div class="legend-title">O<sub>t</sub></div>
                    <div class="legend-line"></div>
                </div>
                <div class="legend trapecia last" :style="{'height': `${getLegendHeight('top-trape')}px`}">
                    <div class="legend-title">T<sub>t</sub></div>
                    <div class="legend-line"></div>
                </div>
                <div class="placeholder-legends-left"></div>
                <div class="legend trapecia" :style="{'height': `${getLegendHeight('bot-trape')}px`, 'opacity': getOpacity()}">
                    <div class="legend-title">T<sub>b</sub></div>
                    <div class="legend-line"></div>
                </div>
                <div class="legend ot" :style="{'height': `${getLegendHeight('bot-rect')}px`, 'opacity': getOpacity()}">
                    <div class="legend-title">O<sub>b</sub></div>
                    <div class="legend-line"></div>
                </div>
                <div class="fill">
                    <div class="legend depth last" :style="{'height': `${getLegendHeight('bot-depth')}px`, 'display': isDepthVisible('bot'), 'opacity': getOpacity()}">
                        <div class="legend-title">P<sub>b</sub></div>
                        <div class="legend-line"></div>
                    </div>
                </div>
            </div>
            <!-- Drawing -->
            <div class="layer support">
                <div class="fill top">
                    <div class="rect depth" :style="{'height': `${getTopDepthHeight()}px`, 'display': isDepthVisible('top'), 'width': `${getWidth('top')}px`}"></div>
                </div>
                <div class="rect" :style="{'height': `${getTopRectHeight()}px`, 'width': `${getWidth('top')}px`}"></div>
                <div class="rect upper trapecia" :style="{'border-bottom-width': `${getTopTrapeHeight()}px`, 'border-left-width': getTrapeSkew('top'), 'border-right-width': getTrapeSkew('top'), 'width': `${getWidth('top')}px`}">
                    <div class="legend-line"></div>
                    <div class="legend-title">W<sub>t</sub></div>
                </div>
                <div class="rect main" :class="{'build-plate': buildPlate}"></div>
                <div class="rect bottom trapecia" v-if="!buildPlate" :style="{'border-top-width': `${getBotTrapeHeight()}px`, 'border-left-width': getTrapeSkew('bot'), 'border-right-width': getTrapeSkew('bot'), 'width': `${getWidth('bot')}px`}">
                    <div class="legend-title">W<sub>b</sub></div>
                    <div class="legend-line"></div>
                </div>
                <div class="rect" v-if="!buildPlate" :style="{'height': `${getBotRectHeight()}px`, 'width': `${getWidth('bot')}px`}"></div>
                <div class="fill bottom">
                    <div class="rect depth" :style="{'height': `${getBotDepthHeight()}px`, 'display': isDepthVisible('bot'), 'width': `${getWidth('bot')}px`, 'opacity': getOpacity()}"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
  /** Set minimum and maximum value for a number */
  const range = (min, max) => number => {
    if (number >= min && number <= max) {
      return number
    } else if (number < min) {
      return min
    } else if (number > max) {
      return max
    }
  }

  export default {
    props: ['params', 'buildPlate'],
    data: function () {
      return {
        legendVertDiff: 24,
        /**
        Data for proportion computing:
        Array [milimetersNum, pixelsNum]
        */
        trapecia: {
          med: [0.5, 50]
        },
        depth: {
          max: [0.5, 69]
        },
        rect: {
          max: [1, 50]
        }
      }
    },
    methods: {
      getWidth (side) {
        const {wt, wb} = this.params
        const restrict = range(20, 100)
        const widthTop = restrict(Number(wt.current))
        let widthBot = restrict(Number(wb.current))
        if (this.buildPlate) {
          widthBot = restrict(Number(wb.default))
        }
        const floor2 = num => Math.floor(num / 2) * 2
        switch (side) {
          case 'top':
            return floor2(110 * widthTop / 100)
          case 'bot':
            return floor2(110 * widthBot / 100)
          default:
            return 30
        }
      },
      /** Trapezoids */
      getTopTrapeHeight () {
        const [medMm, medPx] = this.trapecia.med
        const {tt: {current: enteredMm}} = this.params
        const restrict = range(24, 90)
        return restrict(
          (medPx * enteredMm) / medMm
        )
      },
      getBotTrapeHeight () {
        const [medMm, medPx] = this.trapecia.med
        let enteredMm = this.params.tb.current
        if (this.buildPlate) {
          enteredMm = this.params.tb.default
        }
        const restrict = range(24, 90)
        return restrict(
          (medPx * enteredMm) / medMm
        )
      },
      getTrapeSkew (side) {
        const countTrapeSkew = enteredWidth => Math.floor((110 - enteredWidth) / 2)
        return `${countTrapeSkew(this.getWidth(side))}px`
      },
      /** Depths */
      getTopDepthHeight () {
        const [maxMm, maxPx] = this.depth.max
        const {pt: {current: enteredMm}} = this.params
        const restrict = range(24, maxPx)
        return restrict(maxPx * enteredMm / maxMm)
      },
      getBotDepthHeight () {
        const [maxMm, maxPx] = this.depth.max
        let enteredMm = this.params.pb.current
        if (this.buildPlate) {
          enteredMm = this.params.pb.default
        }
        const restrict = range(0, maxPx)
        return restrict(maxPx * enteredMm / maxMm)
      },
      isDepthVisible (side) {
        const {pt, pb} = this.params
        const hideIfHeightIs0 = height => height === 0 ? 'none' : 'inherit'
        switch (side) {
          case 'top':
            return hideIfHeightIs0(Number(pt.current))
          case 'bot':
            if (this.buildPlate) {
              return hideIfHeightIs0(Number(pb.default))
            }
            return hideIfHeightIs0(Number(pb.current))
          default:
            return 'flex'
        }
      },
      /** Rectangles */
      getTopRectHeight () {
        const [maxMm, maxPx] = this.rect.max
        const {ot: {current: enteredMm}} = this.params
        const restrict = range(0, maxPx)
        return restrict(maxPx * enteredMm / maxMm)
      },
      getBotRectHeight () {
        const [maxMm, maxPx] = this.rect.max
        let enteredMm = this.params.ob.current
        if (this.buildPlate) {
          enteredMm = this.params.ob.default
        }
        const restrict = range(0, maxPx)
        return restrict(maxPx * enteredMm / maxMm)
      },
      /** Legends; White gap. */
      getLegendHeight (place) {
        const {legendVertDiff} = this
        switch (place) {
          case 'top-trape':
            return this.getTopTrapeHeight() - legendVertDiff + 2
          case 'bot-trape':
            return this.getBotTrapeHeight() - legendVertDiff + 2
          case 'top-depth':
            return this.getTopDepthHeight() - legendVertDiff + 1
          case 'bot-depth':
            return this.getBotDepthHeight() - legendVertDiff + 3
          case 'top-rect':
            return this.getTopRectHeight() - legendVertDiff
          case 'bot-rect':
            return this.getBotRectHeight() - legendVertDiff
          default:
            return 1
        }
      },
      getBaseGapHeight () {
        let gapHeight = this.getBotTrapeHeight() +
          this.getTopTrapeHeight() +
          this.getBotRectHeight() +
          this.getTopRectHeight() +
          110
        if (!this.buildPlate) {
          gapHeight -= 4
        }
        return gapHeight
      },
      getOpacity () {
        return this.buildPlate ? 0 : 1
      }
    } // End methods
  }
</script>

<style lang="scss" scoped>
$bg-gray: #f2f2f2;
$baseColor: #00e9f6;
$rectColor: #000;
$trapeColor: #888;
$mainRectColor: #eee;
$legendSpanColor: #000;
$legendBorder: 2px dashed #222;

$upperHeight: 20px;
$upperDepth: 20px;
$baseGap: 110px + 80px + 80px + 40px + 40px;

.illustration {
  height: 100%;
  background-color: $bg-gray;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  position: relative;
  min-height: 600px;

  &.centered {
    justify-content: center;
  }

  .canvas {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: $baseColor;

    .base-gap {
      background: #fff;
      height: $baseGap;
      width: 100%;
    }

    .layer {
      position: absolute;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      flex-direction: column;

      &.support {
        top: 0px;
        align-items: center;
      }

      &.legends {
        align-items: center;
        right: 60px;
        top: -2px;
      }

      &.block-names {
        justify-content: space-between;
        top: 0;

          .top-name, .bottom-name {
            font-size: 1.8rem;
            text-transform: uppercase;
          }

          .top-name {
            padding-top: 40px;
          }

          .bottom-name {
            padding-bottom: 40px;
          }
      }
    }

    .fill {
      flex: 1;
      height: 100%; width: 100%;
    }

    .fill {
      display: flex;
      flex-direction: column;
      align-items: center;

      &.top {
        justify-content: flex-end;
      }

      &.bottom {
        justify-content: flex-start;
      }
    }


    // Figures
    .base {
      background: $baseColor;
      width: 100%;
    }

    .rect {
      $cubeHeight: 110px !global;
      $rectWidth: 40px;
      $trapeciaShift: ($cubeHeight - $rectWidth) / 2;
      $trapeciaHeight: 80px !global;

      width: $rectWidth;
      height: $cubeHeight;
      background: $rectColor;

      &.trapecia {
        background: transparent;
        border-left: $trapeciaShift solid transparent;
        border-right: $trapeciaShift solid transparent;
        height: 0;
        box-sizing: content-box;

        &.upper {
          border-bottom: $trapeciaHeight solid $trapeColor;
        }

        &.bottom {
          border-top: $trapeciaHeight solid $trapeColor;
        }

        .legend-line {
          width: 100%;
          height: 2px;
          background: #000;
          margin-top: 5px;
          display: flex;
          justify-content: space-between;

          &:before,
          &:after {
            content: "";
            width: 0;
            height: 0;
            border-left: 5px solid transparent;
            border-right: 5px solid transparent;
            border-bottom: 7px solid;
            position: relative;
            top: -3px;
            right: 3px;
            transform: rotate(-90deg);
          }

          &:after {
            transform: rotate(90deg);
            left: 3px;
          }
        }

        &.bottom {
          position: relative;

          .legend-title, .legend-line {
            position: relative;
            bottom: 35px;
          }
        }
      }

      &.main {
        background: $mainRectColor;
        width: $cubeHeight;
        height: $cubeHeight;

        &.build-plate {
          height: ($cubeHeight + 90px);
        }
      }
    }
    // End Figures

    .legend {
      display: flex;
      position: relative;
      font-size: 20px;
      font-family: Arial, sans-serif;
      padding: 10px 0px;
      font-weight: bold;
      border-top: $legendBorder;
      width: 90px;
      box-sizing: content-box;

      &.last {
        border-bottom: $legendBorder;
      }

      &.trapecia {
        height: $trapeciaHeight - 24px; // WTF? .legend padding & border?
      }

      &.ot {
        height: 19px;
      }

      &.depth {
        height: 47px;
      }

      .legend-line {
        $triangleHeightBord: 7px solid $legendSpanColor;
        display: flex;
        justify-content: space-between;
        flex-direction: column;
        width: 2px;
        height: 100%;
        background: $legendSpanColor;
        position: absolute;
        top: 0;
        left: 28px;

        &:before,
        &:after {
          content: "";
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-bottom: $triangleHeightBord;
          position: relative;
          right: 4px;
        }

        &:after {
          border-bottom: none;
          border-top: $triangleHeightBord;
        }
      }

      .legend-title {
        width: 20px;
        align-self: center;
      }
    }

    .placeholder-legends-left {
      height: $cubeHeight - 1px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// SupportCanvas.vue?642e2429